
import React, { useCallback, useMemo, useState, useEffect, memo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { LoadingIndicator } from "@/components/booking/LoadingIndicator";
import { useOptimizedDayAvailability } from "@/hooks/calendar-slots/use-optimized-day-availability";
import { isSameDayInTimezone, getTodayInTimezone, isTodayInTimezone, getDateKeyInTimezone } from "@/lib/timezone/date-utils";

interface DatePickerStepProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date | null) => void;
  isDayAvailable?: (date: Date) => boolean;
  availableSlots?: string[];
  className?: string;
  hostUserId?: string;
  troubleshootMode?: boolean;
  loading?: boolean;
  shouldShowSkeleton?: boolean;
  enhancedAvailableSlots?: Record<string, { preferred: string[]; available: string[] }>;
  organizerTimezone?: string;
}

export const DatePickerStep: React.FC<DatePickerStepProps> = memo(({
  selectedDate,
  onDateSelect,
  isDayAvailable: externalIsDayAvailable,
  availableSlots = [],
  className,
  hostUserId,
  troubleshootMode = false,
  loading = false,
  shouldShowSkeleton = false,
  enhancedAvailableSlots,
  organizerTimezone = 'UTC'
}) => {
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Use optimized day availability as fallback
  const { 
    isDayAvailable: optimizedIsDayAvailable, 
    preloadCalendarMonth,
    isDateLoading,
    loadedDatesCount
  } = useOptimizedDayAvailability(hostUserId);

  // Memoized enhanced availability check for performance with timezone-aware date keys
  const enhancedIsDayAvailable = useCallback((date: Date): boolean => {
    if (!enhancedAvailableSlots) return false;
    
    // Use timezone-aware date key generation instead of UTC
    const dateKey = getDateKeyInTimezone(date, organizerTimezone);
    
    if (troubleshootMode) {
      console.log('DatePickerStep - Enhanced availability check for date:', date.toISOString());
      console.log('DatePickerStep - Date key (timezone-aware):', dateKey);
      console.log('DatePickerStep - Organizer timezone:', organizerTimezone);
    }
    
    const daySlots = enhancedAvailableSlots[dateKey];
    if (daySlots) {
      const totalSlots = daySlots.preferred.length + daySlots.available.length;
      
      if (troubleshootMode) {
        console.log('DatePickerStep - Total slots for', dateKey, ':', totalSlots);
      }
      
      return totalSlots > 0;
    }
    return false;
  }, [enhancedAvailableSlots, organizerTimezone, troubleshootMode]);

  // Use enhanced availability if provided, otherwise fall back to external or optimized
  const isDayAvailable = useCallback((date: Date): boolean => {
    // Use external function first (which handles enhanced slots)
    if (externalIsDayAvailable) {
      return externalIsDayAvailable(date);
    }
    
    // Fallback to enhanced slots check
    if (enhancedAvailableSlots) {
      return enhancedIsDayAvailable(date);
    }
    
    // Final fallback to optimized availability
    return optimizedIsDayAvailable(date);
  }, [externalIsDayAvailable, enhancedAvailableSlots, enhancedIsDayAvailable, optimizedIsDayAvailable]);
  
  useEffect(() => {
    // Show skeleton for first 300ms minimum to prevent flash
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Pre-load current month when component mounts, starting from today in organizer's timezone
    const startDate = selectedDate || getTodayInTimezone(organizerTimezone);
    preloadCalendarMonth(startDate);
  }, [selectedDate, preloadCalendarMonth, organizerTimezone]);

  // Get today in organizer's timezone
  const today = useMemo(() => {
    return getTodayInTimezone(organizerTimezone);
  }, [organizerTimezone]);

  // Memoized date disabled function to prevent excessive re-renders
  const isDateDisabled = useCallback((date: Date): boolean => {
    // Disable past dates using timezone-aware comparison
    if (date < today) {
      return true;
    }
    
    // For debug user, allow all future dates
    if (hostUserId === 'debug') {
      return false;
    }
    
    // Use the availability function
    const isAvailable = isDayAvailable(date);
    
    if (troubleshootMode) {
      console.log('DatePickerStep - Date disabled check for:', date.toISOString());
      console.log('DatePickerStep - Is available:', isAvailable);
      console.log('DatePickerStep - Will be disabled:', !isAvailable);
    }
    
    return !isAvailable;
  }, [today, isDayAvailable, hostUserId, troubleshootMode]);

  const handleDateSelect = useCallback((date: Date | undefined) => {
    if (troubleshootMode && date) {
      console.log('DatePickerStep - Date selected:', date.toISOString());
      console.log('DatePickerStep - Date key (timezone-aware):', getDateKeyInTimezone(date, organizerTimezone));
    }
    onDateSelect(date || null);
  }, [onDateSelect, troubleshootMode, organizerTimezone]);

  // Remove skeleton here - let parent handle initial loading
  // Only show subtle loading indicator when refreshing data
  // if ((initialLoading && shouldShowSkeleton) || (shouldShowSkeleton && loadedDatesCount === 0)) {
  //   return <LoadingIndicator variant="skeleton" />;
  // }

  return (
    <div className={cn("space-y-6", className)}>
      {/* <div>
        <h2 className="text-lg text-center font-medium text-gray-900 mb-2">
          Select date and time
        </h2>
      </div> */}

      {/* Show subtle loading indicator while dates are being loaded */}
      {loading && !shouldShowSkeleton && (
        <LoadingIndicator variant="refresh" />
      )}

      <div className="">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          disabled={isDateDisabled}
          onMonthChange={(month) => {
            // Pre-load the new month when user navigates
            preloadCalendarMonth(month);
          }}
          defaultMonth={selectedDate || today} // Start calendar from selected date or today in organizer's timezone
        />
      </div>

      {troubleshootMode && selectedDate && process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
          <p><strong>Debug Info:</strong></p>
          <p>Selected: {selectedDate.toDateString()}</p>
          <p>Selected Date Key (timezone-aware): {getDateKeyInTimezone(selectedDate, organizerTimezone)}</p>
          <p>Available (function): {isDayAvailable(selectedDate).toString()}</p>
          <p>Host User ID: {hostUserId || 'Not provided'}</p>
          <p>Organizer Timezone: {organizerTimezone}</p>
          <p>Today in Timezone: {today.toDateString()}</p>
          <p>Is Today: {isTodayInTimezone(selectedDate, organizerTimezone).toString()}</p>
          <p>Loaded Dates: {loadedDatesCount}</p>
          <p>Date Loading: {isDateLoading(selectedDate).toString()}</p>
          {enhancedAvailableSlots && (
            <div className="mt-2">
              <p><strong>Enhanced Slots (first 5 entries):</strong></p>
              {Object.entries(enhancedAvailableSlots).slice(0, 5).map(([dateKey, slots]) => (
                <p key={dateKey}>
                  {dateKey}: {slots.preferred.length} preferred, {slots.available.length} available
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

DatePickerStep.displayName = 'DatePickerStep';
